import { MarkdownViewer } from "@client/components/round/allocator/MarkdownViewer";
import { Button } from "@client/components/utils/Button";
import RecusedBadge from "@client/components/utils/RecusedBadge";
import { Tooltip } from "@client/components/utils/Tooltip";
import { getMembersWithNotes } from "@client/utils/getUserEvaluatorsWithNotes";
import { isEffectivelyRecused } from "@client/utils/interest";
import { pushRecentProposal } from "@client/utils/recentProposals";
import {
  getAllProposalForms,
  getEvaluableByShortname,
  getProposal,
} from "@engine/round/accessors";
import { Node, RoundMember } from "@engine/round/aggregate";
import { Evaluable } from "@lib/types";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { useContext, useEffect, useId, useRef } from "react";
import { useSearchParams } from "wouter";
import { DispatchContext, StateContext } from "../../../../pages/round/Round";
import { LongformEditor } from "../../../utils/LongformEditor";
import { Tab, Tabs } from "../../../utils/Tabs";
import { ProposalInfoPage } from "./ProposalInfoPage";

function queryToWouterHref(params: { [key: string]: string }) {
  const currentUrl = new URL(window.location.href);
  const urlSearchParams = new URLSearchParams(currentUrl.search);
  for (const [key, value] of Object.entries(params)) {
    urlSearchParams.set(key, value);
  }
  return `~${currentUrl.pathname}?${urlSearchParams.toString()}${currentUrl.hash || ""}`;
}

function OrgPageHeading({ evaluable }: { evaluable: Evaluable }) {
  const [, setSearchParams] = useSearchParams();
  return (
    <div className="flex flex-row mx-8">
      <div className="flex justify-between items-center gap-5">
        <h3 className="text-xl">{evaluable.shortname}</h3>
      </div>
      <Button classes="ml-auto" onClick={() => setSearchParams({})}>
        Close
      </Button>
    </div>
  );
}

export function OrgPage({ evaluableId }: { evaluableId: string }) {
  const { round, actingAsUserId: userId } = useContext(StateContext);
  const evaluable = getEvaluableByShortname(round, evaluableId);
  const [searchParams] = useSearchParams();

  if (!evaluable) {
    throw new Error(`Could not find proposal: ${evaluableId}`);
  }
  pushRecentProposal(evaluable.id);

  const notesTabMatch = searchParams.get("tab") === "notes";

  const tabPage: React.ReactNode = notesTabMatch ? (
    <NotesPage
      evaluable={evaluable}
      selectedEvaluatorInitials={
        searchParams.get("evaluator") || round.members[userId].initials
      }
    />
  ) : (
    <div className="px-8">
      <InfoPage evaluable={evaluable} />
    </div>
  );

  return (
    <div className="flex flex-col w-full pt-8 absolute inset-0">
      <OrgPageHeading evaluable={evaluable} />
      <Tabs>
        <Tab
          href={queryToWouterHref({
            proposal: evaluableId,
            tab: "info",
          })}
          selected={!notesTabMatch}
        >
          Info
        </Tab>
        <Tab
          href={queryToWouterHref({
            proposal: evaluableId,
            tab: "notes",
          })}
          selected={notesTabMatch}
        >
          Notes
        </Tab>
      </Tabs>
      <div className="overflow-auto">{tabPage}</div>
    </div>
  );
}

function NotesPage({
  evaluable,
  selectedEvaluatorInitials,
}: {
  evaluable: Node;
  selectedEvaluatorInitials: string | null;
}) {
  const stateContext = useContext(StateContext);
  const { round, actingAsUserId: userId, mode } = stateContext;
  const membersWithNotes = getMembersWithNotes(
    round,
    evaluable.id,
    userId,
    mode,
  );
  const userTrack = round.members[userId].track;

  // track users can only see their own notes until onboarding is complete
  let showOtherNotes = true;
  if (userTrack !== "NO_TRACK") {
    const userTrackStage = round.trackSettings[userTrack].status;
    showOtherNotes = userTrackStage !== "1.onboarding";
  }

  let notePage: React.ReactNode | null;
  if (showOtherNotes && selectedEvaluatorInitials) {
    const selectedMember = membersWithNotes.find(
      (m) => m.member.initials === selectedEvaluatorInitials,
    );

    if (!selectedMember) {
      console.error(
        `Failed to find evaluator: ${selectedEvaluatorInitials}, this should not be possible`,
      );
      notePage = (
        <div>Failed to find evaluator {selectedEvaluatorInitials}</div>
      );
    } else {
      notePage = (
        <NotePage evaluable={evaluable} member={selectedMember.member} />
      );
    }
  } else {
    notePage = (
      <NotePage evaluable={evaluable} member={round.members[userId]} />
    );
  }

  const tabs = showOtherNotes && (
    <Tabs>
      {membersWithNotes.map(({ member, canAccess }) => (
        <Tab
          key={member.id}
          disabled={!canAccess}
          selected={selectedEvaluatorInitials === member.initials}
          href={queryToWouterHref({
            proposal: evaluable.shortname,
            tab: "notes",
            evaluator: member.initials,
          })}
        >
          {canAccess ? (
            member.initials
          ) : (
            <Tooltip title="This user entered notes but they are not shared with recommenders from other tracks">
              {member.initials}
            </Tooltip>
          )}
        </Tab>
      ))}
    </Tabs>
  );

  return (
    <>
      <div className="sticky top-0 light:bg-stone-50 dark:bg-stone-900">
        {tabs}
      </div>
      <div className="mx-8 mt-8">{notePage}</div>
    </>
  );
}

function NotePage({
  evaluable,
  member,
}: {
  evaluable: Node;
  member: RoundMember;
}) {
  const stateContext = useContext(StateContext);
  if (member.id === stateContext.actingAsUserId) {
    return <NoteEditor member={member} evaluable={evaluable} />;
  } else {
    return <NoteViewer member={member} evaluable={evaluable} />;
  }
}

function NoteViewer({
  member: member,
  evaluable,
}: {
  member: RoundMember;
  evaluable: Node;
}) {
  const { round } = useContext(StateContext);
  const feedback = member.evaluableFeedback[evaluable.id];
  const evaluatorDescription = member.initials;
  const disclosure = round.proposalDisclosure[`${evaluable.id}%${member.id}`];
  const isRecused = isEffectivelyRecused(disclosure);

  return (
    <div>
      <h4 className="text-xl mt-4">
        {`${evaluatorDescription}'s Notes`}
        {isRecused && <RecusedBadge className="ml-2" />}
      </h4>
      <MarkdownViewer value={member.evaluableNotes[evaluable.id]} />
      {feedback && feedback.positiveNegative && (
        <>
          <h4 className="text-xl mt-4">{`${evaluatorDescription}'s Positive Feedback`}</h4>
          <MarkdownViewer value={feedback.positiveNegative.positive} />
          <h4 className="text-xl mt-4">{`${evaluatorDescription}'s Negative Feedback`}</h4>
          <MarkdownViewer value={feedback.positiveNegative.negative} />
        </>
      )}
    </div>
  );
}

function NoteEditor({
  member,
  evaluable,
}: {
  member: RoundMember;
  evaluable: Node;
}) {
  const { round } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const id = useId();
  const visibility = member.evaluablePermissions[evaluable.id] ?? "default";

  const feedback = member.evaluableFeedback[evaluable.id]?.positiveNegative ?? {
    type: "positiveNegative",
    positive: "",
    negative: "",
  };

  const disclosure = round.proposalDisclosure[`${evaluable.id}%${member.id}`];
  const isRecused = isEffectivelyRecused(disclosure);

  async function handleNotesChange(newNotes: string) {
    await dispatch({
      type: "EVALUATION_NOTES_CHANGE",
      evaluableId: evaluable.id,
      memberId: member.id,
      notes: newNotes,
    });
  }

  async function handleFeedbackChange(
    newFeedback: string,
    type: "positive" | "negative",
  ) {
    await dispatch({
      type: "EVALUATION_FEEDBACK_CHANGE",
      evaluableId: evaluable.id,
      memberId: member.id,
      [type]: newFeedback,
    });
  }

  async function handleVisibilityChange(
    visibility: "default" | "visible" | "hidden",
  ) {
    await dispatch({
      type: "NOTES_AND_FEEDBACK_VISIBILITY_CHANGE",
      evaluableId: evaluable.id,
      memberId: member.id,
      visibility,
    });
  }

  return (
    <div>
      {isRecused && (
        <div className="mb-2">
          <RecusedBadge />
        </div>
      )}
      <LongformEditor
        label={`Your notes on ${evaluable.type === "proposal" ? evaluable.longname : evaluable.shortname}`}
        value={member.evaluableNotes[evaluable.id] ?? ""}
        setValue={handleNotesChange}
      />
      <div>
        <br />
        <br />
        Applicants really like getting feedback, so please provide feedback (at
        least positive feedback) on every applicant you evaluate with a positive
        (non-zero) curve. Feedback on other applicants is also welcome.
        <br />
        <br />
        Applicants will be able to see the prompts below that we are giving you
        to solicit for feedback (including this statement), so they will know to
        interpret your feedback as responses to these particular prompts, rather
        than as unconstrained/unfiltered impressions.
        <br />
        <br />
        <br />
        <b>1.</b> Positive feedback [required for positive evals]
        <br />
        <br />
        Here, please provide constructive feedback to the applicant
        organization, regarding aspects of the organization that you are
        particularly optimistic about, or things you could envision them doing
        well. In this section, avoid negative impressions or things you think
        they would do poorly.
      </div>
      <LongformEditor
        label=""
        value={feedback.positive ?? ""}
        setValue={(newFeedback) =>
          handleFeedbackChange(newFeedback, "positive")
        }
      />
      <div>
        <br />
        <b>2.</b> Negative feedback: [optional]
        <div className="pl-5">
          <b>1.</b> Commitment to follow up: When receiving negative feedback,
          applicants often have follow-up questions, which can be frustrating
          for them if they don't get a response. So, only provide feedback if
          you can also commit to following up with the organization afterward if
          they have questions about your feedback. Options for following up will
          include:
          <div className="pl-5">
            <b>a.</b> Responding to the applicant's questions directly over
            email, with sff-contact@googlegroups.com cc'd
            <br />
            <b>b.</b> Responding anonymously through an intermediary (e.g.,
            sff-contact@googlegroups.com) who relays your messages
            <br />
            <b>c.</b> (not yet available) Responding anonymously through an app
            interface that conceals your identity.
          </div>
          <br />
          <b>2.</b> Politeness: In case you are mistaken, please qualify any
          negative feedback as your impressions ("When I saw [X], I got the
          impression that [Y]"), rather than as confident assertions of what is
          true about the organization. Avoid making confident statements about
          what the organization “should” do, or how they should change to
          accommodate your impression, because you probably lack a lot of
          context about their opportunity costs in terms of other avenues for
          doing good.
        </div>
      </div>
      <LongformEditor
        label=""
        value={feedback.negative ?? ""}
        setValue={(newFeedback) =>
          handleFeedbackChange(newFeedback, "negative")
        }
      />

      <div className="pt-4 pb-16">
        <h4 className="text-xl">Notes and Feedback Sharing</h4>
        <div>
          <input
            id={`${id}-notes-shared-default`}
            type="radio"
            value="default"
            checked={visibility === "default"}
            onChange={() => void handleVisibilityChange("default")}
          />{" "}
          <label htmlFor={`${id}-notes-shared-default`}>
            Default from User Settings (currently{" "}
            <em>{member.feedbackVisibleByDefault ? "visible" : "hidden"}</em>)
          </label>
        </div>
        <div>
          <input
            id={`${id}-notes-shared-visible`}
            type="radio"
            value="visible"
            checked={visibility === "visible"}
            onChange={() => void handleVisibilityChange("visible")}
          />{" "}
          <label htmlFor={`${id}-notes-shared-visible`}>
            Visible to evaluators from other tracks
          </label>
        </div>
        <div>
          <input
            id={`${id}-notes-shared-hidden`}
            type="radio"
            value="hidden"
            checked={visibility === "hidden"}
            onChange={() => void handleVisibilityChange("hidden")}
          />{" "}
          <label htmlFor={`${id}-notes-shared-hidden`}>
            Hidden from evaluators from other tracks
          </label>
        </div>
      </div>
    </div>
  );
}

function InfoPage({ evaluable }: { evaluable: Node }) {
  const { round } = useContext(StateContext);
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0;
    }
  }, [evaluable.id]);

  switch (evaluable.type) {
    case "proposal": {
      const proposal = getProposal(round, evaluable.id);
      if (!proposal) {
        return <div>Failed to find proposal, this should not be possible</div>;
      }

      const proposalForms = getAllProposalForms(round);

      const proposalForm = proposalForms.find(
        (proposalForm) => proposalForm.proposals[evaluable.id],
      );

      if (!proposalForm) {
        return (
          <div>Failed to find proposal form, this should not be possible</div>
        );
      }

      return (
        <div className="overflow-auto" ref={scrollableRef}>
          <ProposalInfoPage
            proposal={proposal}
            filteredProposalForm={proposalForm}
          />
        </div>
      );
    }
    case "recommender":
      return <div>Recommender info</div>;
    case "funder":
      return <div>Funder info</div>;
    default:
      assertUnreachable(evaluable);
  }
}
